import { useRef, useState } from 'react';
import { uploadMediaFile } from '@/hooks/useUpload';
import type { DeckChart, DeckTable } from '@/lib/slideDeck';

export type InsertKind = 'image' | 'video' | 'table' | 'chart' | 'link';

interface SlideInsertDialogsProps {
  kind: InsertKind | null;
  onClose: () => void;
  onImage: (src: string, link?: string) => void;
  onVideo: (url: string) => void;
  onTable: (table: DeckTable) => void;
  onChart: (chart: DeckChart) => void;
  onLink: (url: string, label: string) => void;
}

const CHART_PRESETS: { id: string; label: string; chart: DeckChart }[] = [
  {
    id: 'bar',
    label: 'Ventes par secteur',
    chart: {
      kind: 'bar',
      title: 'Ventes par secteur',
      categories: ['Services', 'Produits', 'Formation', 'Abonnements'],
      values: [42, 28, 18, 12],
    },
  },
  {
    id: 'pie',
    label: 'Répartition du chiffre d’affaires',
    chart: {
      kind: 'pie',
      title: 'Répartition du chiffre d’affaires',
      categories: ['Boutique', 'Marketplace', 'Prestations'],
      values: [52, 31, 17],
    },
  },
  {
    id: 'line',
    label: 'Évolution mensuelle',
    chart: {
      kind: 'line',
      title: 'Évolution des revenus',
      categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin'],
      values: [12, 18, 16, 24, 31, 38],
    },
  },
];

export default function SlideInsertDialogs({
  kind,
  onClose,
  onImage,
  onVideo,
  onTable,
  onChart,
  onLink,
}: SlideInsertDialogsProps) {
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [linkLabel, setLinkLabel] = useState('');
  const [chart, setChart] = useState<DeckChart>(CHART_PRESETS[0].chart);
  const [chartCsv, setChartCsv] = useState('Services;42\nProduits;28\nFormation;18\nAbonnements;12');
  const fileRef = useRef<HTMLInputElement>(null);

  if (!kind) return null;

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const url = await uploadMediaFile(file, 'documents/presentations');
      setImageUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'envoi de l'image");
    } finally {
      setUploading(false);
    }
  };

  const applyChart = () => {
    const lines = chartCsv
      .split(/\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const categories: string[] = [];
    const values: number[] = [];
    lines.forEach((line) => {
      const parts = line.split(/[;\t,]/);
      const label = (parts[0] || '').trim();
      const value = Number((parts[1] || '0').replace(',', '.'));
      if (label) categories.push(label);
      values.push(Number.isFinite(value) ? value : 0);
    });
    onChart({ ...chart, categories, values });
  };

  const titles: Record<InsertKind, string> = {
    image: 'Insérer une image',
    video: 'Insérer une vidéo',
    table: 'Insérer un tableau',
    chart: 'Insérer un graphique',
    link: 'Insérer un lien',
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-background-50 rounded-xl p-5 max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold font-heading text-foreground-950">{titles[kind]}</h3>
          <button onClick={onClose} className="w-7 h-7 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {kind === 'image' && (
          <div className="space-y-3">
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed border-background-200/70 bg-white text-sm text-foreground-600 hover:border-primary-300 cursor-pointer"
              disabled={uploading}
            >
              {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-cloud-2-line text-xl"></i>}
              {uploading ? 'Envoi en cours…' : 'Choisir une image sur mon appareil'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                void pickFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <div>
              <label className="block text-[11px] font-medium text-foreground-600 mb-1">Ou coller une URL d'image</label>
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…"
                className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-white text-xs text-foreground-800"
              />
            </div>
            {error && <p className="text-[11px] text-red-600">{error}</p>}
            <button
              onClick={() => {
                if (!imageUrl.trim()) return;
                onImage(imageUrl.trim());
              }}
              disabled={!imageUrl.trim() || uploading}
              className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium disabled:opacity-50 cursor-pointer hover:bg-primary-600 whitespace-nowrap"
            >
              Insérer l'image
            </button>
          </div>
        )}

        {kind === 'video' && (
          <div className="space-y-3">
            <label className="block text-[11px] font-medium text-foreground-600">Lien YouTube ou URL vidéo</label>
            <input
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
              className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-white text-xs text-foreground-800"
            />
            <button
              onClick={() => videoUrl.trim() && onVideo(videoUrl.trim())}
              disabled={!videoUrl.trim()}
              className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium disabled:opacity-50 cursor-pointer hover:bg-primary-600 whitespace-nowrap"
            >
              Insérer la vidéo
            </button>
          </div>
        )}

        {kind === 'table' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1">Colonnes</label>
                <input
                  type="number"
                  min={1}
                  max={8}
                  value={tableCols}
                  onChange={(e) => setTableCols(Math.max(1, Math.min(8, Number(e.target.value) || 1)))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-white text-xs"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1">Lignes</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={tableRows}
                  onChange={(e) => setTableRows(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                  className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-white text-xs"
                />
              </div>
            </div>
            <button
              onClick={() =>
                onTable({
                  headers: Array.from({ length: tableCols }, (_, i) => `Colonne ${i + 1}`),
                  rows: Array.from({ length: tableRows }, () => Array.from({ length: tableCols }, () => '')),
                })
              }
              className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 whitespace-nowrap"
            >
              Créer le tableau
            </button>
          </div>
        )}

        {kind === 'chart' && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-1">
              {(['bar', 'pie', 'line'] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setChart({ ...chart, kind: k })}
                  className={`py-2 rounded-md text-xs font-medium cursor-pointer ${chart.kind === k ? 'bg-accent-100 text-accent-800' : 'bg-white border border-background-200/70 text-foreground-600'}`}
                >
                  {k === 'bar' ? 'Barres' : k === 'pie' ? 'Camembert' : 'Courbe'}
                </button>
              ))}
            </div>
            <div>
              <label className="block text-[11px] font-medium text-foreground-600 mb-1">Titre</label>
              <input
                value={chart.title}
                onChange={(e) => setChart({ ...chart, title: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-white text-xs"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-foreground-600 mb-1">Données (une ligne : libellé;valeur)</label>
              <textarea
                value={chartCsv}
                onChange={(e) => setChartCsv(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-white text-xs font-mono resize-none"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {CHART_PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setChart(p.chart);
                    setChartCsv(p.chart.categories.map((c, i) => `${c};${p.chart.values[i]}`).join('\n'));
                  }}
                  className="px-2.5 py-1 rounded-full border border-background-200/70 bg-white text-[10px] text-foreground-600 hover:bg-background-100 cursor-pointer"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              onClick={applyChart}
              className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 whitespace-nowrap"
            >
              Insérer le graphique
            </button>
          </div>
        )}

        {kind === 'link' && (
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-foreground-600 mb-1">Adresse</label>
              <input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-white text-xs"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-foreground-600 mb-1">Texte affiché</label>
              <input
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
                placeholder="Découvrir le site"
                className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-white text-xs"
              />
            </div>
            <button
              onClick={() => linkUrl.trim() && onLink(linkUrl.trim(), linkLabel.trim() || linkUrl.trim())}
              className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 whitespace-nowrap"
            >
              Insérer le lien
            </button>
          </div>
        )}
      </div>
    </div>
  );
}